import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { useUsers } from '../context/UserContext';

// Map views to required permissions
const VIEW_PERMISSIONS = {
  // Orders
  'dashboard': ['orders.view'],
  'orders': ['orders.view'],
  'orders-new': ['orders.view'],
  'order-detail': ['orders.view'],
  'order-history': ['orders.view'],
  'phone-order': ['orders.create'],
  'calendar': ['orders.view'],

  // Menu & Inventory
  'menu': ['menu.view'],
  'menu-editor': ['menu.view'],
  'list-all-items': ['menu.view'],
  'inventory': ['inventory.manage'],
  'stock-alerts': ['inventory.manage'],

  // Customers
  'customers': ['customers.view'],
  'reviews': ['reviews.view'],
  'chat': ['customers.view'],

  // Delivery
  'delivery-partners': ['delivery.view'],
  'delivery-tracking': ['delivery.track'],

  // Finance
  'payouts': ['finance.view'],
  'transactions': ['finance.view'],
  'invoices': ['finance.invoices'],

  // Analytics & Reports
  'analytics': ['analytics.view'],
  'business-reports': ['analytics.reports'],
  'live-tracking': ['analytics.view'],

  // Settings & Admin
  'settings': ['settings.view'],
  'users': ['users.view'],
  'role-permissions': ['users.permissions'],
  'data-management': ['data.manage'],
  'manage-locations': ['settings.edit'],

  // Kitchen Display
  'kitchen-display': ['orders.view', 'orders.status'],

  // Offers
  'create-offers': ['menu.create'],
  'track-offers': ['menu.view'],

  // Outlet Info
  'about-outlet': ['settings.view'],
  'contact-details': ['settings.view'],
  'outlet-timings': ['settings.view'],
  'outlet-location': ['settings.view'],
  'smart-link': ['settings.view'],

  // Utility pages (always accessible)
  'thermal-printer': [],
  'menu-migration': ['menu.edit']
};

// Default permissions by role
const DEFAULT_ROLE_PERMISSIONS = {
  admin: ['*'], // Admin has all permissions
  manager: [
    'orders.view', 'orders.create', 'orders.edit', 'orders.status', 'orders.cancel', 'orders.assign',
    'menu.view', 'menu.create', 'menu.edit', 'menu.availability', 'inventory.manage',
    'customers.view', 'customers.edit', 'reviews.view', 'reviews.respond',
    'delivery.view', 'delivery.track', 'delivery.assign',
    'analytics.view', 'analytics.reports',
    'settings.view'
  ],
  kitchen: [
    'orders.view', 'orders.status',
    'menu.view', 'menu.availability',
    'inventory.manage'
  ],
  delivery: [
    'orders.view',
    'delivery.view', 'delivery.track'
  ]
};

export const usePermissions = () => {
  const { currentUser } = useAuth();
  const { users } = useUsers();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get current user's role - match by UID (more reliable than email)
  const currentUserData = users.find(u => u.id === currentUser?.uid);
  // Normalize role to lowercase to ensure consistency
  const userRole = currentUserData?.role?.toLowerCase() || 'delivery'; // Default to lowest privilege

  // Debug logging
  useEffect(() => {
    if (currentUser && users.length > 0) {
      console.log('🔍 Current user UID:', currentUser?.uid);
      console.log('👥 Users in context:', users.length);
      console.log('📋 Current user data:', currentUserData);
      console.log('🎭 Determined role:', userRole);
    }
  }, [currentUser, users, currentUserData, userRole]);

  // Get display name for role
  const getRoleDisplayName = (role) => {
    const roleMap = {
      'admin': 'Admin - Full Access',
      'manager': 'Manager',
      'kitchen': 'Kitchen Staff',
      'delivery': 'Delivery Partner'
    };
    return roleMap[role] || role;
  };

  // Load saved permissions from Firestore
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const permissionsDoc = await getDoc(doc(db, 'settings', 'role_permissions'));
        if (permissionsDoc.exists()) {
          setPermissions(permissionsDoc.data());
        } else {
          setPermissions(DEFAULT_ROLE_PERMISSIONS);
        }
      } catch (error) {
        console.error('Error loading permissions:', error);
        setPermissions(DEFAULT_ROLE_PERMISSIONS);
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, []);

  // Check if current user has a specific permission
  const hasPermission = useCallback((permission) => {
    if (!permissions || !userRole) return false;

    const rolePermissions = permissions[userRole] || DEFAULT_ROLE_PERMISSIONS[userRole] || [];

    // Admin with '*' has all permissions
    if (rolePermissions.includes('*')) return true;

    return rolePermissions.includes(permission);
  }, [permissions, userRole]);

  // Check if user can access a specific view
  const canAccessView = useCallback((viewName) => {
    const requiredPermissions = VIEW_PERMISSIONS[viewName];

    // If no permissions required, allow access
    if (!requiredPermissions || requiredPermissions.length === 0) return true;

    // Check if user has any of the required permissions
    return requiredPermissions.some(perm => hasPermission(perm));
  }, [hasPermission]);

  // Get list of accessible views for current user
  const getAccessibleViews = useCallback(() => {
    return Object.keys(VIEW_PERMISSIONS).filter(view => canAccessView(view));
  }, [canAccessView]);

  return {
    loading,
    userRole,
    userRoleDisplay: getRoleDisplayName(userRole),
    currentUserData,
    hasPermission,
    canAccessView,
    getAccessibleViews,
    permissions
  };
};

export { VIEW_PERMISSIONS, DEFAULT_ROLE_PERMISSIONS };
